import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "@phosphor-icons/react";
import { scrollToSection } from "@/components/Reveal";

const HERO_IMG = "https://images.pexels.com/photos/31623485/pexels-photo-31623485.jpeg";

const LINES = [
  { text: "Successful travel", italic: false },
  { text: "businesses are built on", italic: false },
  { text: "trusted partnerships.", italic: true },
];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.16, delayChildren: 0.35 } },
};

const line = {
  hidden: { y: "115%" },
  show: { y: "0%", transition: { duration: 1, ease: [0.22, 1, 0.36, 1] } },
};

export default function Hero() {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 240]);
  const contentOpacity = useTransform(scrollY, [0, 550], [1, 0.15]);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col justify-end overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0" aria-hidden="true">
        <img
          src={HERO_IMG}
          alt="Mount Kazbek, Georgia"
          className="absolute -top-[10%] left-0 h-[120%] w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#071526]/60 via-[#071526]/30 to-[#071526]" />
      </motion.div>

      <motion.div
        style={{ opacity: contentOpacity }}
        className="relative z-10 mx-auto w-full max-w-[1440px] px-6 pb-14 pt-44 md:px-12 md:pb-20"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.15 }}
          data-testid="hero-eyebrow"
          className="mb-8 flex items-center gap-3 text-xs font-medium uppercase tracking-[0.3em] text-[#A8C6E8]"
        >
          <span className="h-px w-10 bg-[#A8C6E8]/70" />
          B2B Travel Partnerships — Georgia
        </motion.p>

        <motion.h1
          variants={container}
          initial="hidden"
          animate="show"
          data-testid="hero-heading"
          className="font-display text-4xl font-light leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-6xl"
        >
          {LINES.map((l) => (
            <span key={l.text} className="block overflow-hidden pb-1">
              <motion.span
                variants={line}
                className={`block ${l.italic ? "italic text-[#A8C6E8]" : ""}`}
              >
                {l.text}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
          data-testid="hero-subcopy"
          className="mt-8 max-w-xl text-sm font-light leading-relaxed tracking-wide text-[#E6ECF3] md:text-base"
        >
          WOFERS GLOBAL partners with travel agencies, tour operators, DMCs, corporate
          travel companies and independent advisors worldwide — delivering seamless
          travel experiences across Georgia, and real value for our partners.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.3, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#contact"
            data-testid="hero-partner-cta"
            onClick={(e) => scrollToSection(e, "#contact")}
            className="group inline-flex items-center gap-2 bg-[#184167] px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-[#112E4A]"
          >
            Explore Partnership
            <ArrowUpRight
              size={16}
              weight="bold"
              className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </a>
          <a
            href="#services"
            data-testid="hero-services-cta"
            onClick={(e) => scrollToSection(e, "#services")}
            className="inline-flex items-center gap-2 border border-white/40 px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-[#A8C6E8] hover:text-[#A8C6E8]"
          >
            Our Services
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.7 }}
          className="mt-16 flex flex-wrap items-end justify-between gap-6 border-t border-white/15 pt-6"
        >
          <div className="flex flex-wrap gap-x-12 gap-y-4" data-testid="hero-stats">
            {[
              ["08", "Service lines"],
              ["05", "Partner types"],
              ["01", "Local expert team"],
            ].map(([num, label]) => (
              <div key={label}>
                <div className="font-display text-3xl font-light text-white">{num}</div>
                <div className="mt-1 text-[10px] uppercase tracking-[0.25em] text-[#C6D2E0]">
                  {label}
                </div>
              </div>
            ))}
          </div>
          <div
            data-testid="hero-scroll-cue"
            className="flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-[#C6D2E0]"
          >
            Scroll
            <motion.span
              animate={{ y: [0, 6, 0] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            >
              <ArrowDown size={14} weight="light" />
            </motion.span>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
